import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, TrendingUp, PiggyBank, Target, Users, Zap } from 'lucide-react';

export default function Services() {
  const scrollToSection = (sectionId) => {
    // Add a small delay to ensure page is fully rendered
    setTimeout(() => {
      const element = document.querySelector(sectionId);
      if (element) {
        console.log(`Scrolling to ${sectionId}`, element);
        element.scrollIntoView({ 
          behavior: "smooth",
          block: "start",
          inline: "nearest"
        });
      } else {
        console.warn(`Element with id "${sectionId}" not found`);
        // Fallback: try to find any element with similar id
        const allElements = document.querySelectorAll('[id*="booking"]');
        console.log('Elements with "booking" in id:', allElements);
      }
    }, 100);
  };

  const services = [
    {
      icon: <PiggyBank className="h-8 w-8" />,
      title: "Retirement Planning",
      description: "Build a secure retirement with tax-free strategies and comprehensive planning that ensures your golden years are truly golden.",
      features: ["Tax-free growth", "Comprehensive planning", "Risk management", "Income optimization"]
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Financial Protection",
      description: "Protect your family's future with comprehensive insurance and protection strategies that provide peace of mind.",
      features: ["Life insurance", "Disability protection", "Long-term care", "Asset protection"]
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: "Wealth Building",
      description: "Grow your wealth through tax-advantaged capital accumulation and protected growth strategies designed for long-term legacy success.",
      features: ["Tax-advantaged growth", "Capital protection", "Protected growth vehicles", "Legacy planning"]
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Goal Planning",
      description: "Achieve your financial goals with personalized strategies tailored to your unique situation and aspirations.",
      features: ["Goal setting", "Action plans", "Progress tracking", "Milestone celebration"]
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Family Planning",
      description: "Secure your family's financial future with comprehensive planning that protects and grows your legacy.",
      features: ["Estate planning", "Education funding", "Generational wealth", "Family protection"]
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Tax Strategies",
      description: "Minimize your tax burden with advanced strategies that keep more of your hard-earned money in your pocket.",
      features: ["Tax reduction", "Strategic planning", "Compliance support", "Optimization"]
    }
  ];

  return (
    <section id="services" className="relative py-24 bg-[#0D1B2A] text-white overflow-hidden">
      {/* ── Background Mesh Lighting ───────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-1/3 -right-32 w-[500px] h-[500px] bg-[#C9A84C]/10 rounded-full blur-[150px] animate-pulse-glow" />
        <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] bg-[#3D6ACC]/15 rounded-full blur-[150px]" />
        <div 
          className="absolute inset-0 opacity-[0.05]" 
          style={{
            backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs sm:text-sm font-semibold text-[#E8D4A0] tracking-wider uppercase">
            <Shield className="w-4 h-4 text-[#C9A84C]" />
            Wealth & Life Solutions
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-playfair tracking-tight">
            Comprehensive <span className="text-gradient-gold">Financial Protection & Growth</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto font-opensans leading-relaxed">
            Tailored financial architecture engineered to build, protect, and transfer wealth safely across generations.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index} 
              className="group relative bg-[#162236]/90 border border-[#C9A84C]/25 hover:border-[#C9A84C]/60 hover:-translate-y-2 transition-all duration-300 rounded-2xl p-8 shadow-2xl backdrop-blur-xl flex flex-col justify-between"
            >
              <div>
                <div className="mb-6 w-16 h-16 rounded-2xl bg-gradient-to-br from-[#162236] to-[#0D1B2A] border border-[#C9A84C]/30 flex items-center justify-center text-[#C9A84C] group-hover:scale-110 group-hover:border-[#C9A84C] group-hover:shadow-[0_0_20px_rgba(201,168,76,0.3)] transition-all duration-300">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold font-playfair text-white mb-3 group-hover:text-[#E8D4A0] transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed mb-6">
                  {service.description}
                </p>
              </div>

              <div className="pt-4 border-t border-slate-700/60 space-y-2.5">
                {service.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center gap-2.5 text-xs sm:text-sm text-slate-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action Banner */}
        <div className="mt-16 text-center">
          <div className="max-w-3xl mx-auto bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-8 sm:p-10 shadow-2xl backdrop-blur-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#C9A84C]/10 rounded-full blur-3xl pointer-events-none" />
            <h3 className="text-2xl sm:text-3xl font-bold font-playfair text-white mb-3">
              Ready to Architect Your Financial Legacy?
            </h3>
            <p className="text-slate-300 text-sm sm:text-base mb-6 max-w-xl mx-auto">
              Schedule a confidential 1-on-1 strategy session with our licensed professionals to build your custom Financial Needs Analysis.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => scrollToSection("#booking")}
                className="px-8 py-3.5 bg-gradient-gold-cta text-[#070E1B] rounded-xl font-bold text-base shadow-xl hover:opacity-95 transition-all duration-300"
              >
                Schedule Free Consultation
              </button>
              <Link 
                to="/blog" 
                className="px-8 py-3.5 border border-[#C9A84C]/50 text-[#E8D4A0] rounded-xl font-semibold hover:bg-[#C9A84C]/10 transition-all duration-300 inline-block text-center"
              >
                Explore Knowledge Hub
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}